import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MessageCircle, Send, Trash2, Shield } from "lucide-react";
import { toast } from "sonner";
import { Footer } from "../components/Footer";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_8cb5d32c-b1ce-4951-b77e-897f98e6cc9f/artifacts/uyctypm7_41ed1e03-8bc9-42a0-8314-b43b52fec9a9.JPG";

export default function TopicDetailPage() {
  const { topicId } = useParams();
  const [topic, setTopic] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNamePrompt, setShowNamePrompt] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [savingName, setSavingName] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchTopic();
    fetchComments();
    checkAuth();
  }, [topicId]);

  const checkAuth = async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
        // Check if display name needs to be set
        if (!data.display_name_set && data.role === "fan") {
          setShowNamePrompt(true);
          setDisplayName(data.name || "");
        }
      }
    } catch { /* not logged in - expected for anonymous visitors */ }
  };

  const handleSetDisplayName = async (e) => {
    e.preventDefault();
    if (!displayName.trim() || displayName.trim().length < 2) {
      toast.error("Display name must be at least 2 characters");
      return;
    }
    setSavingName(true);
    try {
      const res = await fetch(`${API_URL}/api/fan-pass/display-name`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ display_name: displayName.trim() })
      });
      if (res.ok) {
        setUser(prev => ({ ...prev, name: displayName.trim(), display_name_set: true }));
        setShowNamePrompt(false);
        toast.success("Display name set!");
      }
    } catch {
      toast.error("Failed to save name");
    } finally {
      setSavingName(false);
    }
  };

  const fetchTopic = async () => {
    try {
      const res = await fetch(`${API_URL}/api/forum/topics/${topicId}`);
      if (res.ok) {
        const data = await res.json();
        setTopic(data);
        document.title = `${data.title} - Afro Fanthem Forum`;
      }
    } catch {
      // topic fetch failed
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const res = await fetch(`${API_URL}/api/forum/topics/${topicId}/comments`);
      if (res.ok) {
        const data = await res.json();
        setComments(data.comments);
      }
    } catch {
      // comments fetch failed
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/forum/topics/${topicId}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ content: newComment.trim() })
      });
      if (res.ok) {
        const comment = await res.json();
        setComments(prev => [...prev, comment]);
        setNewComment("");
        setTopic(prev => prev ? { ...prev, comment_count: (prev.comment_count || 0) + 1 } : prev);
        toast.success("Comment posted!");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to post comment");
      }
    } catch (e) {
      toast.error("Failed to post comment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      const res = await fetch(`${API_URL}/api/forum/comments/${commentId}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (res.ok) {
        setComments(prev => prev.filter(c => c.comment_id !== commentId));
        setTopic(prev => prev ? { ...prev, comment_count: Math.max(0, (prev.comment_count || 1) - 1) } : prev);
        toast.success("Comment deleted");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to delete");
      }
    } catch (e) {
      toast.error("Failed to delete comment");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-10 h-10 border-3 border-[#FBBF24] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!topic) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="font-heading text-4xl text-white uppercase mb-4">Topic Not Found</h1>
          <Link to="/forum" className="text-[#FBBF24] hover:underline">Back to Forum</Link>
        </div>
      </div>
    );
  }

  const CATEGORY_LABELS = {
    "diaspora-supporters": "Diaspora Supporters",
    "music-chants-anthems": "Music, Chants and Anthems",
    "fan-stories": "Fan Stories",
    "afcon-african-football": "AFCON / African Football"
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Afro Fanthem" className="h-10 md:h-14 w-auto" />
            </Link>
            <div className="flex items-center gap-4 sm:gap-6">
              <Link to="/forum" className="text-gray-300 hover:text-white text-sm transition-colors">Forum</Link>
              <Link to="/survey" className="bg-[#FBBF24] text-black font-bold uppercase tracking-widest px-4 sm:px-6 py-2 sm:py-3 text-sm hover:bg-[#F59E0B] transition-all">
                Take the Survey
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <main className="pt-24 md:pt-32 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          <Link to="/forum" className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-sm transition-colors mb-6">
            <ArrowLeft className="w-4 h-4" />
            Back to Forum
          </Link>

          {/* Topic Header */}
          <article className="bg-[#141414] border border-white/10 mb-8" data-testid="topic-detail">
            <div className="p-6 sm:p-8">
              <span className="text-[#FBBF24] text-xs uppercase tracking-wider font-bold">
                {CATEGORY_LABELS[topic.category_id] || topic.category_id}
              </span>
              <h1 className="font-heading text-3xl sm:text-4xl text-white uppercase tracking-tight mt-2 mb-4" data-testid="topic-title">
                {topic.title}
              </h1>
              {topic.content && (
                <p className="text-gray-300 leading-relaxed">{topic.content}</p>
              )}
              <div className="flex items-center gap-4 mt-6 text-sm text-gray-500">
                <span>by <span className="text-gray-300">{topic.author_name}</span></span>
                <span>{new Date(topic.created_at).toLocaleDateString()}</span>
                <span className="flex items-center gap-1"><MessageCircle className="w-4 h-4" /> {topic.comment_count} comments</span>
              </div>
            </div>
          </article>

          {/* Comments */}
          <section data-testid="comments-section">
            <h2 className="font-heading text-xl text-white uppercase mb-4">
              Comments ({comments.length})
            </h2>

            {comments.length === 0 && (
              <div className="bg-white/5 border border-white/10 p-8 text-center mb-6">
                <p className="text-gray-400">No comments yet. Be the first to share your thoughts.</p>
              </div>
            )}

            <div className="space-y-4 mb-8">
              {comments.map((comment) => (
                <div key={comment.comment_id} className="bg-white/5 border border-white/10 p-5" data-testid={`comment-${comment.comment_id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <p className="text-gray-200 leading-relaxed">{comment.content}</p>
                      <div className="flex items-center gap-3 mt-3 text-sm text-gray-500">
                        <span className="text-gray-400">{comment.author_name}</span>
                        {comment.author_type === "admin" && (
                          <span className="bg-[#FBBF24]/10 text-[#FBBF24] text-xs px-2 py-0.5 uppercase tracking-wider font-bold">Admin</span>
                        )}
                        <span>{new Date(comment.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    {user?.role === "admin" && (
                      <button
                        onClick={() => handleDeleteComment(comment.comment_id)}
                        className="text-gray-600 hover:text-red-500 transition-colors p-1"
                        title="Delete comment"
                        data-testid={`delete-comment-${comment.comment_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Comment Form */}
            {user && showNamePrompt ? (
              <div className="bg-[#141414] border border-[#FBBF24]/30 p-6" data-testid="display-name-prompt">
                <h3 className="text-white font-bold uppercase tracking-wider text-sm mb-2">Choose Your Display Name</h3>
                <p className="text-gray-400 text-sm mb-4">This is what other fans will see. You can change it later.</p>
                <form onSubmit={handleSetDisplayName} className="flex gap-3">
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Your display name"
                    maxLength={40}
                    className="flex-1 bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-[#FBBF24]/50 focus:outline-none"
                    data-testid="display-name-input"
                  />
                  <button type="submit" disabled={savingName || displayName.trim().length < 2} className="bg-[#FBBF24] text-black font-bold uppercase tracking-widest px-6 py-3 text-sm hover:bg-[#F59E0B] transition-all disabled:opacity-50" data-testid="save-display-name-btn">
                    {savingName ? "Saving..." : "Set Name"}
                  </button>
                </form>
              </div>
            ) : user ? (
              <form onSubmit={handleSubmitComment} className="bg-[#141414] border border-white/10 p-6" data-testid="comment-form">
                <label htmlFor="comment-input" className="text-white font-bold uppercase tracking-wider text-sm mb-3 block">
                  Add Your Comment
                </label>
                <textarea
                  id="comment-input"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Share your thoughts..."
                  className="w-full bg-white/5 border border-white/10 text-white p-4 min-h-[100px] focus:border-[#FBBF24]/50 focus:outline-none transition-colors resize-y"
                  data-testid="comment-input"
                />
                <div className="flex justify-end mt-4">
                  <button
                    type="submit"
                    disabled={submitting || !newComment.trim()}
                    className="inline-flex items-center gap-2 bg-[#FBBF24] text-black font-bold uppercase tracking-widest px-6 py-3 text-sm hover:bg-[#F59E0B] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="submit-comment-btn"
                  >
                    {submitting ? "Posting..." : "Post Comment"}
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </form>
            ) : (
              <div className="bg-[#141414] border border-white/10 p-6 text-center" data-testid="comment-auth-prompt">
                <Shield className="w-8 h-8 text-[#FBBF24] mx-auto mb-3" />
                <p className="text-gray-300 mb-2 font-medium">
                  Join the movement before joining the debate.
                </p>
                <p className="text-gray-400 text-sm mb-6">
                  Complete the African Football Fan Survey to unlock your Fan Pass and start commenting. No full profile needed.
                </p>
                <div className="flex gap-4 justify-center">
                  <Link to="/forum/join" className="bg-[#FBBF24] text-black font-bold uppercase tracking-widest px-6 py-3 text-sm hover:bg-[#F59E0B] transition-all" data-testid="comment-get-access">
                    Get Forum Access
                  </Link>
                </div>
              </div>
            )}
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
